import { registerPlugin } from "@capacitor/core";
import { isNativeApp } from "@/lib/runtime";
import { computeHeatBonuses } from "@/lib/computeHeatBonuses";

const FileExport = registerPlugin("CsvExport");

const STATUS_LABELS = {
  completed: "OK",
  excluded: "Utesluten",
  X: "Utesluten",
  M: "Tvåminutersuteslutning",
  N: "Ej start",
  R: "Brutit",
  FX: "Utesluten",
  F: "Fall",
  FN: "Fall, ej vållande",
  TT: "Tapes",
};

function filenamePart(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
}

function filename(savedMatch) {
  const match = savedMatch?.match_details || {};
  const teams = [match.home_team, match.away_team].map(filenamePart).filter(Boolean).join("-mot-") || "matchprotokoll";
  const date = match.date ? String(match.date).slice(0, 10) : "utan-datum";
  return `lapstr-${teams}-${date}.pdf`;
}

function formatDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Datum saknas";
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Stockholm",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function heatRows(heat, homeTeam, awayTeam) {
  const bonuses = computeHeatBonuses(heat) || {};
  return Object.entries(heat?.riders || {})
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([gate, rider]) => {
      const result = (heat?.results || []).find((entry) => String(entry?.rider_id || "") === String(rider?.rider_id || ""));
      return {
        gate,
        rider: rider?.name || result?.rider_name || "Okänd förare",
        team: rider?.team === "home" ? homeTeam : rider?.team === "away" ? awayTeam : "",
        points: result?.points ?? "-",
        bonus: bonuses?.[String(rider?.rider_id || "")] ?? result?.bonus_points ?? 0,
        status: STATUS_LABELS[result?.status] || result?.status || "-",
      };
    });
}

async function createPdf(savedMatch) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const match = savedMatch?.match_details || {};
  const results = savedMatch?.user_results || {};
  const homeTeam = match.home_team || "Hemmalag";
  const awayTeam = match.away_team || "Bortalag";
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = 18;

  const ensureSpace = (height) => {
    if (y + height <= pageHeight - 15) return;
    doc.addPage();
    y = 18;
  };
  const text = (value, x, yy, size = 9, style = "normal") => {
    doc.setFont("helvetica", style);
    doc.setFontSize(size);
    doc.text(String(value ?? ""), x, yy);
  };

  doc.setTextColor(201, 37, 45);
  text("LAPSTR MATCHPROTOKOLL", 14, y, 16, "bold");
  doc.setTextColor(20, 25, 28);
  y += 10;
  text(`${homeTeam}  ${results.home_score ?? 0} - ${results.away_score ?? 0}  ${awayTeam}`, 14, y, 15, "bold");
  y += 7;
  doc.setTextColor(90, 96, 100);
  text(formatDate(match.date), 14, y, 9);
  y += 10;

  [...(results.heats || [])]
    .sort((a, b) => Number(a?.heat_number || 0) - Number(b?.heat_number || 0))
    .forEach((heat) => {
      const rows = heatRows(heat, homeTeam, awayTeam);
      ensureSpace(17 + rows.length * 7);
      doc.setDrawColor(201, 37, 45);
      doc.setLineWidth(0.7);
      doc.line(14, y, 196, y);
      y += 6;
      doc.setTextColor(20, 25, 28);
      text(`HEAT ${heat?.heat_number ?? ""}`, 14, y, 11, "bold");
      if (heat?.heat_time) text(`Heattid ${heat.heat_time}`, 154, y, 9);
      y += 6;
      doc.setFillColor(238, 239, 240);
      doc.rect(14, y - 4.5, 182, 6, "F");
      text("Bana", 16, y, 8, "bold");
      text("Förare", 32, y, 8, "bold");
      text("Lag", 98, y, 8, "bold");
      text("P", 147, y, 8, "bold");
      text("B", 158, y, 8, "bold");
      text("Status", 169, y, 8, "bold");
      y += 6;
      rows.forEach((row, index) => {
        if (index % 2 === 1) {
          doc.setFillColor(248, 248, 248);
          doc.rect(14, y - 4.5, 182, 6, "F");
        }
        text(row.gate, 17, y, 8);
        text(doc.splitTextToSize(row.rider, 61)[0], 32, y, 8);
        text(doc.splitTextToSize(row.team, 43)[0], 98, y, 8);
        text(row.points, 148, y, 8);
        text(row.bonus, 159, y, 8);
        text(doc.splitTextToSize(row.status, 25)[0], 169, y, 8);
        y += 6;
      });
      y += 4;
    });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setTextColor(120, 125, 128);
    text(`Lapstr · sida ${page} av ${pageCount}`, 14, pageHeight - 8, 8);
  }

  return { data: doc.output("datauristring").split(",")[1], filename: filename(savedMatch) };
}

function base64Blob(data, mimeType) {
  const binary = window.atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type: mimeType });
}

export async function saveProtocolPdf(savedMatch) {
  const file = await createPdf(savedMatch);
  if (isNativeApp()) {
    return FileExport.saveBinary({ ...file, mimeType: "application/pdf" });
  }
  const url = URL.createObjectURL(base64Blob(file.data, "application/pdf"));
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  return { filename: file.filename, location: "Hämtade filer" };
}

export async function shareProtocolPdf(savedMatch) {
  const file = await createPdf(savedMatch);
  if (!isNativeApp()) {
    const browserFile = new File([base64Blob(file.data, "application/pdf")], file.filename, { type: "application/pdf" });
    if (navigator.share && navigator.canShare?.({ files: [browserFile] })) {
      await navigator.share({ title: "Matchprotokoll", files: [browserFile] });
      return { filename: file.filename };
    }
    return saveProtocolPdf(savedMatch);
  }
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  const stored = await Filesystem.writeFile({ path: file.filename, data: file.data, directory: Directory.Cache });
  await Share.share({
    title: "Exportera matchprotokoll",
    text: "Matchprotokoll exporterat från Lapstr.",
    files: [stored.uri],
    dialogTitle: "Dela matchprotokoll",
  });
  return { filename: file.filename };
}
